import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { Gavi } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { color, radio, texto } from '#components/mobile/ui/tokens';

import type { FueraDelBarco } from './fueraDelBarco';

const seAleja = keyframes({
  to: { transform: 'translate(120px, -12px) scale(.55)', opacity: 0.5 },
});
const alejarse = css({
  transformBox: 'fill-box',
  animation: `${seAleja} 3s .4s cubic-bezier(.4,0,.3,1) forwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/**
 * «Ya no estás a bordo»: lo que ve quien ha sido bajado de un presupuesto, en
 * vez de un error de sincronización. El velero del capitán se aleja y el
 * tuyo se queda en el muelle.
 */
export function PantallaFueraDelBarco({
  datos,
  onCerrar,
}: {
  datos: FueraDelBarco;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const capitan = datos.capitan ?? t('The captain');
  return (
    <View
      role="dialog"
      aria-modal
      aria-label={t('You are no longer aboard')}
      data-testid="fuera-del-barco"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        backgroundColor: color.bg,
        alignItems: 'center',
        padding: '40px 22px calc(36px + env(safe-area-inset-bottom))',
        textAlign: 'center',
        overflowY: 'auto',
      }}
    >
      <svg
        viewBox="0 0 300 170"
        aria-hidden
        style={{ width: 300, maxWidth: '100%', height: 'auto' }}
      >
        <path
          d="M0 118 q25 -8 50 0 t50 0 t50 0 t50 0 t50 0 t50 0 V170 H0 Z"
          fill={color.accent}
          opacity={0.18}
        />
        <rect x="10" y="100" width="92" height="10" rx="2" fill="#c9a46a" />
        <rect x="20" y="110" width="7" height="34" fill="#c9a46a" />
        <rect x="80" y="110" width="7" height="34" fill="#c9a46a" />
        <g className={alejarse}>
          <g transform="translate(170 56)">
            <line
              x1="26"
              y1="2"
              x2="26"
              y2="50"
              stroke={color.fg}
              strokeWidth={1.8}
            />
            <path d="M27 5 L50 44 L27 44 Z" fill={color.ok} />
            <path d="M4 50 H54 L46 62 H12 Z" fill={color.fg} />
          </g>
        </g>
        <g transform="translate(40 64)">
          <line
            x1="22"
            y1="2"
            x2="22"
            y2="40"
            stroke={color.fg}
            strokeWidth={1.8}
          />
          <path d="M23 5 L41 35 L23 35 Z" fill={color.accent} />
          <path d="M4 40 H46 L39 50 H11 Z" fill={color.fg} />
        </g>
        <path
          d="M0 128 q25 -7 50 0 t50 0 t50 0 t50 0 t50 0 t50 0 V170 H0 Z"
          fill={color.accent}
          opacity={0.32}
        />
      </svg>
      <Text style={{ ...texto.display, fontSize: 21, margin: '4px 0 6px' }}>
        {datos.presupuesto
          ? t('You are no longer aboard «{{name}}»', {
              name: datos.presupuesto,
            })
          : t('You are no longer aboard')}
      </Text>
      <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
        {t(
          '{{captain}} took you off this plan. We removed it from your phone; your other budgets stay the same.',
          { captain: capitan },
        )}
      </Text>
      <View
        style={{
          marginTop: 16,
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: 8,
          textAlign: 'left',
        }}
      >
        <Gavi ancho={52} style={{ flexShrink: 0 }} />
        <View
          style={{
            backgroundColor: color.surface,
            borderRadius: `${radio.tarjeta - 4}px ${radio.tarjeta - 4}px ${radio.tarjeta - 4}px 4px`,
            padding: '10px 12px',
          }}
        >
          <Text style={{ ...texto.secundario, fontWeight: 600 }}>
            {t(
              'If it was a mistake, ask {{captain}} for another link and you are back aboard. I have no opinion on this: I only charge the mooring fee.',
              { captain: capitan },
            )}
          </Text>
        </View>
      </View>
      <View style={{ flex: 1, minHeight: 24 }} />
      <Boton bloque data-testid="ir-al-puerto" onPress={onCerrar}>
        <Trans>Go to the harbour</Trans>
      </Boton>
    </View>
  );
}
